import React, {useContext, useEffect, useMemo, useState} from "react";
import classNames from "classnames";
import pick from 'lodash.pick';
import {CSSTransition} from 'react-transition-group';
import {FacebookShareButton, TwitterShareButton,} from "react-share";
import {
	IconCase,
	IconFacebook,
	IconMBO,
	IconRAM,
	IconSecondCPU,
	IconSecondFAN,
	IconSecondGPU,
	IconSecondPSU,
	IconSoundCard,
	IconSSD,
	IconTwitter
} from "../../contants/icons";
import {isSmallDevice} from '../../helpers/index';
import IconButton from "../IconButton/IconButton";
import ConditionalWrapper from "../ConditionalWrapper/ConditionalWrapper";
import ProductPrice, {priceFormat} from "../Product/ProductPrice";
import AppContext from "../../store";
import API from '../../api/index';
import Portal from '../../containers/Portal';
import IconClose from '../IconButton/IconClose/IconClose';
import './SelectionsOverview.scss';
import PropTypes from 'prop-types';
import {
	TEXT_FIND_IN_SHOP,
	TEXT_GOMLVL_LONG,
	TEXT_HAVE_MORE_QUESTIONS,
	TEXT_MORE_QUESTIONS,
	TYPE_CASE,
	TYPE_CHARGE,
	TYPE_CPU,
	TYPE_FAN,
	TYPE_GPU,
	TYPE_MBO,
	TYPE_RAM,
	TYPE_SOUND,
	TYPE_SSD,
} from "../../contants/common";
import {TYPE_SECONDARY} from "../Buttons/ButtonShop";
import ButtonShopRedirect, {redirect} from "../Buttons/ButtonShopRedirect";
import useWindowSize from "../../hooks/useWindowSize";
import ScrollableArea from "../ScrollableArea/ScrollableArea";
import useDeviceSizeDetect from "../../hooks/useDeviceSizeDetect";

const filters = {
	[TYPE_CASE]: item => item.type === TYPE_CASE,
	[TYPE_CPU]: item => item.type === TYPE_CPU,
	[TYPE_MBO]: item => item.type === TYPE_MBO,
	[TYPE_GPU]: item => item.type === TYPE_GPU,
	[TYPE_RAM]: item => item.type === TYPE_RAM,
	[TYPE_FAN]: item => item.type === TYPE_FAN,
	[TYPE_SSD]: item => ~item.category.toLowerCase().indexOf(TYPE_SSD),
	[TYPE_CHARGE]: item => ~item.category.toLowerCase().indexOf(TYPE_CHARGE),
	[TYPE_SOUND]: item => ~item.category.toLowerCase().indexOf(TYPE_SOUND),
};

const iconsData = {
	[TYPE_CASE]: IconCase,
	[TYPE_CPU]: IconSecondCPU,
	[TYPE_MBO]: IconMBO,
	[TYPE_GPU]: IconSecondGPU,
	[TYPE_RAM]: IconRAM,
	[TYPE_FAN]: IconSecondFAN,
	[TYPE_SSD]: IconSSD,
	[TYPE_CHARGE]: IconSecondPSU,
	[TYPE_SOUND]: IconSoundCard,
}

const hashTags = ['#mifcom', '#getonmylvl'];

const TablesLabelWrapper = ({type, children}) => {
	const size = useWindowSize(300);
	
	return (
		<ConditionalWrapper
			children={children}
			condition={isSmallDevice(size.width)}
			wrapper={() => {
				return iconsData[type]
						? <IconButton IconComponent={iconsData[type]} />
						: <p>-</p>
			}}
			altWrapper={child => <div className="category">{child}:</div>}
		/>
	)
}

export default function SelectionsOverview() {
	const {
		options,
		bundles,
		activeBundleId,
		getActiveBundle,
		getTotalPrice,
		getRedirectLink,
		selectedOptions,
		toggleConfiguratorPortal,
		isConfiguratorPortalOpen,
	} = useContext(AppContext);
	const [items, setItems] = useState([]);
	const [title, setTitle] = useState([]);
	const [price, setPrice] = useState('');
	const detectMobile = useDeviceSizeDetect();
	
	const textMoreQuestions = useMemo(() => {
		return detectMobile.isMobile
			? TEXT_MORE_QUESTIONS
			: TEXT_HAVE_MORE_QUESTIONS
	}, [detectMobile]);
	
	const textCustomizeInShop = useMemo(() => {
		return TEXT_FIND_IN_SHOP;
	}, [detectMobile]);
	
	const activeBundleIndex = useMemo(() => {
		return bundles.findIndex(bundle => bundle.bundleId === activeBundleId)
	}, [bundles, activeBundleId]);
	
	const preparedItems = useMemo(() => prepareItems(items),	[items]);
	
	const reduceSelections = () => {
		return Object.entries(selectedOptions || {})
		.reduce((acc, [type, productId]) => {
			const option = options.find(el => el.productId === productId);
			acc.push(pick(option, ['title', 'type', 'id']))
			return acc;
		}, [])
	}

	const updateItems = () => {
		const activeBundle = getActiveBundle();
		if(activeBundle) {
			const {source} = activeBundle;
			const reducedSelections = reduceSelections();
			API.getDefaultSelections(source)
			.then(res => {
				const preparedItems = prepareItems(res);
				const newItems = preparedItems.map(el => ({
					...el,
					...reducedSelections.find(s => s.type === el.type)
				}))
				setItems(newItems)
			})
		}
	}

	function prepareItems(items) {
		return Object.entries(filters)
		.reduce((acc, [key, filter]) => {
			Array.prototype.push.apply(
				acc,
				items
				.filter(filter)
				.map(el => {
					el.type = key;
					return el;
				})
			)
			return acc;
		}, [])
	}

	const linkRedirect = useMemo(getRedirectLink, [
		selectedOptions,
		activeBundleId,
		getRedirectLink,
	]);

	useEffect(() => {
		const bundle = getActiveBundle();
		if(bundle) {
			setTitle(bundle.title);
			setPrice(getTotalPrice());
		}
	}, [activeBundleId, bundles]);

	useEffect(() => {
		if(selectedOptions) {
			updateItems();
			setPrice(getTotalPrice());
		}
	}, [selectedOptions, bundles]);
	
	return (
		<Portal id={"SelectionsOverview"}>
			<CSSTransition
				in={isConfiguratorPortalOpen}
				timeout={500}
			>
				<div className="selections-overview-container container-holder">
					<div className="selections-overview-inner">
						<div className="selections-overview-left">
							<div className={classNames([
								'selections-overview-banner',
								`image-${activeBundleIndex%3}`
							])}></div>
						</div>
						<div className="selections-overview-right">
							<IconClose
								onClick={toggleConfiguratorPortal}
								className={'selections-overview-close'}
							/>
							<div className="selections-overview-title">{TEXT_GOMLVL_LONG} <span className="bold">{title}</span></div>
							<div className="selections-overview-content">
								{
									preparedItems.length &&
									<div className="selections-overview-table">
										<ScrollableArea>
												{
													preparedItems.map((item, i) => (
														<div key={i || item.id} className="selections-overview-row">
															<div className="selections-overview-col">
																<TablesLabelWrapper {...item}>
																	{item.category}
																</TablesLabelWrapper>
															</div>
															<div className="selections-overview-col">
																<div className="title">{item.title.split(' | ')[0]}</div>
															</div>
														</div>
													))
												}
										</ScrollableArea>
									</div>
								}
							</div>

							<div className="selections-overview-advantages">
								<div className="selections-overview-advantages-item">
									<div className="selections-overview-advantages-title">3 jahre garantie</div>
									<div className="selections-overview-advantages-subtitle">inkl. pickup-& return</div>
								</div>
								<div className="selections-overview-advantages-item">
									<div className="selections-overview-advantages-title">0% finanzierung</div>
									<div className="selections-overview-advantages-subtitle">mit consors finanz</div>
								</div>
								<div className="selections-overview-advantages-item">
									<div className="selections-overview-advantages-title">lebelslanger</div>
									<div className="selections-overview-advantages-subtitle">technischer support</div>
								</div>
							</div>
							
							<div className="mobile-landscape-selections-overlay">
								<div className="selections-overview-price">
									ab <ProductPrice {...priceFormat(price)} />
								</div>
	
								<div className="selections-overview-cta">
									<div
										className="selections-overview-question cursor-pointer"
										onClick={() => redirect(linkRedirect)}
									>
										<span>{textMoreQuestions}</span>
									</div>
									<ButtonShopRedirect
										size={TYPE_SECONDARY}
										className={'selections-overview-redirect'}
										text={textCustomizeInShop}/>
								</div>
	
								<div className="selections-share visible-device-big">
									<p className="selections-share-text">Teile deine Erfahrung auf:</p>
									<div className="selections-share-links">
										<FacebookShareButton url={linkRedirect} hashtags={hashTags.join(' ')}>
											<IconButton IconComponent={IconFacebook}/>
										</FacebookShareButton>
										<TwitterShareButton url={linkRedirect} hashtags={hashTags}>
											<IconButton IconComponent={IconTwitter}/>
										</TwitterShareButton>
									</div>
								</div>
							</div>

						</div>
					</div>
				</div>
			</CSSTransition>
		</Portal>
	)
}

TablesLabelWrapper.propTypes = {
	children: PropTypes.oneOfType([
		PropTypes.arrayOf(PropTypes.node),
		PropTypes.node
	]).isRequired,
	type: PropTypes.oneOf([
		TYPE_CASE,
		TYPE_CPU,
		TYPE_MBO,
		TYPE_GPU,
		TYPE_RAM,
		TYPE_FAN,
		TYPE_SSD,
		TYPE_CHARGE,
		TYPE_SOUND,
	]).isRequired,
}
